import { observer } from "mobx-react-lite";
import { useState } from "react";
import styled from "styled-components";

import { count } from "../../count";
import { useStores } from "../../stores/useStores";
import { color } from "../../theme/color";
import { Button, Pill, SmallHeading } from "../ui/controls";
import { PaneHeader, PaneTitle } from "../ui/lists";
import { PhoneBack } from "../ui/PhoneBack";
import { AddToSetlistDialog } from "./AddToSetlistDialog";

/** A song and its versions. */
export const SongDetail = observer(function SongDetail({ songId }: { songId: string }) {
    const { library, navigation } = useStores();
    const [withSetlistOnly, setWithSetlistOnly] = useState(false);
    const [addingToSetlist, setAddingToSetlist] = useState(false);
    const song = library.song(songId);

    if (!song) return null;

    const versions = library.versionsOf(song.id, withSetlistOnly);
    const setlistOnlyCount = library.versionsOf(song.id, true).length - library.versionsOf(song.id).length;

    async function remove() {
        if (!confirm(`Delete "${song!.title}" and all its versions?`)) return;

        try {
            await library.change((l) => l.deleteSong(songId));
            navigation.replace({ name: "songs" });
        } catch (error) {
            alert((error as Error).message);
        }
    }

    return (
        <>
            <PaneHeader>
                <PhoneBack label="Back to songs" onClick={() => navigation.go({ name: "songs" })} />
                <PaneTitle>{song.title}</PaneTitle>
            </PaneHeader>
            {song.artist && <Artist>{song.artist}</Artist>}
            <Actions>
                <Button $kind="primary" onClick={() => setAddingToSetlist(true)}>
                    Add to a setlist
                </Button>
                <Button $kind="danger" onClick={remove}>
                    Delete song
                </Button>
            </Actions>
            <SmallHeading style={{ padding: "0 16px" }}>Versions</SmallHeading>
            <Versions>
                {versions.map((sheet) => {
                    const copiedFrom = library.chordSheet(sheet.copiedFromId);
                    const playing = library.setlistsPlaying(sheet.id).length;

                    const details = [
                        copiedFrom && `Copied from ${library.title(copiedFrom.id)}`,
                        `changed ${new Date(sheet.changedAt).toLocaleDateString(undefined, { day: "numeric", month: "short" })}`,
                        playing > 0 && `in ${count(playing, "setlist")}`,
                    ].filter(Boolean);

                    return (
                        <li key={sheet.id}>
                            <Version onClick={() => navigation.go({ name: "sheet", chordSheetId: sheet.id, from: { kind: "songs" } })}>
                                <VersionTitle>
                                    {library.title(sheet.id)}
                                    {sheet.setlistId && <Pill $tone="setlistOnly">this setlist only</Pill>}
                                </VersionTitle>
                                <VersionDetail>{details.join(" · ")}</VersionDetail>
                            </Version>
                        </li>
                    );
                })}
            </Versions>
            {setlistOnlyCount > 0 && (
                <ShowSetlistOnly $kind="quiet" onClick={() => setWithSetlistOnly(!withSetlistOnly)}>
                    {withSetlistOnly ? "Hide" : "Show"} {count(setlistOnlyCount, "setlist-only version")}
                </ShowSetlistOnly>
            )}
            {addingToSetlist && <AddToSetlistDialog songId={song.id} onClose={() => setAddingToSetlist(false)} />}
        </>
    );
});

const Artist = styled.p`
    margin: 0;
    padding: 0 16px 16px;
    font-size: 16px;
    color: ${color.textMuted};
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
    padding: 0 16px 24px;
`;

const Versions = styled.ul`
    list-style: none;
    margin: 10px 0 0;
    padding: 0 16px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
    gap: 10px;
`;

const Version = styled.button`
    width: 100%;
    padding: 14px 16px;
    border: 1px solid ${color.border};
    border-radius: 14px;
    background: ${color.background};
    text-align: left;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    gap: 6px;

    &:hover {
        border-color: ${color.borderStrong};
    }
`;

const VersionTitle = styled.span`
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
`;

const VersionDetail = styled.span`
    font-size: 14px;
    color: ${color.textMuted};
`;

const ShowSetlistOnly = styled(Button)`
    align-self: flex-start;
    margin: 8px 4px;
`;
